"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.closePlan = closePlan;
const path = __importStar(require("path"));
const dist_1 = require("../../fs/dist");
const dist_2 = require("../../core/dist");
const planReducer_1 = require("../../core/dist/reducers/planReducer");
/**
 * Finalize a completed plan. This use-case does NOT generate the done-doc body via
 * inference — the agent (Claude itself, on the primary path) authors the implementation
 * record via `loom_append_done` per step. closePlan only:
 *   1. Optionally writes `notes` verbatim into the done doc (closing summary), and
 *   2. Runs the FINISH_PLAN reducer and persists the plan (in-place for a thread plan,
 *      moved to done/ for a flat/loose plan).
 *
 * If neither `notes` nor an existing done doc is present, it throws rather than writing
 * a placeholder stub — a missing done record is a loud failure, never a silent one.
 */
async function closePlan(input, deps) {
    const weaveId = await (0, dist_1.resolveWeaveSlugForPlan)(deps.loomRoot, input.planUlid);
    const weave = await deps.loadWeave(deps.loomRoot, weaveId);
    const plan = weave.threads.flatMap((t) => t.plans).find((p) => p.id === input.planUlid);
    if (!plan)
        throw new Error(`Plan '${input.planUlid}' not found in weave '${weaveId}'.`);
    const thread = weave.threads.find((t) => t.plans.some((p) => p.id === input.planUlid));
    const weavePath = path.join(deps.loomRoot, 'loom', weaveId);
    const threadPath = thread ? path.join(weavePath, thread.id) : null;
    const doneDirPath = threadPath ? path.join(threadPath, 'done') : path.join(weavePath, 'done');
    // Done id stays ULID-derived (stable); filename humanises to plan-NNN-done.md.
    // Dual-read: keep a pre-existing legacy {planId}-done.md so we append to the same file.
    const doneId = `${input.planUlid}-done`;
    const planOrd = plan._path ? (0, dist_2.planOrdinalFromFile)(path.basename(plan._path)) : null;
    const canonicalDoneFile = planOrd !== null ? (0, dist_2.doneFileName)(planOrd) : `${doneId}.md`;
    const legacyDoneFile = `${doneId}.md`;
    let doneBasename = canonicalDoneFile;
    if (canonicalDoneFile !== legacyDoneFile
        && !(await deps.fs.pathExists(path.join(doneDirPath, canonicalDoneFile)))
        && (await deps.fs.pathExists(path.join(doneDirPath, legacyDoneFile)))) {
        doneBasename = legacyDoneFile;
    }
    const donePath = path.join(doneDirPath, doneBasename);
    const doneExists = await deps.fs.pathExists(donePath);
    const notes = input.notes?.trim();
    // No done content and nothing to write — fail loud instead of stubbing.
    if (!notes && !doneExists) {
        throw new Error(`No done content for plan '${input.planUlid}': the done doc does not exist and no notes were provided. ` +
            `Author it with loom_append_done per step, or pass notes to loom_close_plan.`);
    }
    await deps.fs.ensureDir(doneDirPath);
    if (notes) {
        if (doneExists) {
            // Done doc already authored per-step — append the notes as a closing section.
            const existing = await (0, dist_1.loadDoc)(donePath);
            const body = (existing.content ?? '').replace(/\n+$/, '');
            const updated = {
                ...existing,
                content: `${body}\n\n## Closing notes\n\n${notes}\n`,
                version: existing.version + 1,
            };
            await deps.saveDoc(updated, donePath);
        }
        else {
            // No per-step record — the notes become the done doc body verbatim.
            const doneDoc = {
                type: 'done',
                id: doneId,
                title: `Done — ${plan.title}`,
                status: 'done',
                created: (0, dist_2.today)(),
                version: 1,
                tags: [],
                parent_id: input.planUlid,
                requires_load: [],
                content: `\n${notes}\n`,
            };
            await deps.saveDoc(doneDoc, donePath);
        }
    }
    // else: notes absent but done doc exists → leave the per-step record untouched.
    let updatedPlan = plan;
    if (plan.status === 'implementing') {
        updatedPlan = (0, planReducer_1.planReducer)(plan, { type: 'FINISH_PLAN' });
    }
    if (threadPath) {
        // Thread plan: update in place; done doc is the separate record.
        const planPath = plan._path ?? path.join(threadPath, 'plans', `${input.planUlid}.md`);
        await deps.saveDoc(updatedPlan, planPath);
    }
    else {
        // Flat/loose plan: move to done/.
        const newPlanPath = path.join(doneDirPath, `${input.planUlid}.md`);
        await deps.saveDoc(updatedPlan, newPlanPath);
        const oldPlanPath = plan._path;
        if (oldPlanPath && await deps.fs.pathExists(oldPlanPath)) {
            await deps.fs.remove(oldPlanPath);
        }
    }
    return { donePath, planId: input.planUlid };
}
